import type { ImportResumeDto, ResumeDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";

import { syncResumeMutationCache } from "./cache";

const IMPORT_POLL_INTERVAL_MS = 1500;
const IMPORT_POLL_MAX_ATTEMPTS = 20;

type ImportJobStatus =
  | { status: "pending" }
  | { status: "processing" }
  | { status: "completed"; resumeId: string }
  | { status: "failed"; error?: string };

export const importResume = async (data: ImportResumeDto): Promise<ResumeDto> => {
  const enqueueResponse = await axios.post<
    { jobId: string },
    AxiosResponse<{ jobId: string }>,
    ImportResumeDto
  >("/resume/import", data);

  const { jobId } = enqueueResponse.data;

  for (let attempt = 0; attempt < IMPORT_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, IMPORT_POLL_INTERVAL_MS));

    const statusResponse = await axios.get<ImportJobStatus>(`/resume/import/${jobId}/status`);
    const result = statusResponse.data;

    if (result.status === "completed") {
      const resumeResponse = await axios.get<ResumeDto>(`/resume/${result.resumeId}`);
      return resumeResponse.data;
    }
    if (result.status === "failed") {
      throw new Error(result.error ?? "Resume import failed.");
    }
  }

  throw new Error("Resume import timed out. Please try again.");
};

export const useImportResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: importResumeFn,
  } = useMutation({
    mutationFn: importResume,
    onSuccess: async (data) => {
      await syncResumeMutationCache(data);
    },
  });

  return { importResume: importResumeFn, loading, error };
};
